import {
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { AlertComponent, ChipComponent, TypographyComponent } from '@purolator/ui-kit-angular';
import { EstimatesOrchestratorApiService } from '../../../../core/api/estimates-orchestrator-api.service';
import { ProductMasterApiService } from '../../../../core/api/product-master/product-master-api.service';
import type {
  ProductRelation,
  ProductSelectionItem,
} from '../../../../core/api/product-master/models/product-selection.model';
import { ServiceAbilityApiService } from '../../../../core/api/service-ability-api.service';
import { ServiceAvailabilityApiService } from '../../../../core/api/service-availability-api.service';
import { readProductRelations } from '../../forms/resolve-signature-options';
import { AnalyticsService } from '../../../../core/integrations/analytics';
import { TranslatePipe, TranslationService } from '../../../../core/i18n';
import { COUPON_VALIDATION_FAILED } from '../../../../core/models/estimates-orchestrator';
import {
  SHIPMENT_WIZARD_STEPS,
  ShipmentFlowStateService,
  type ShipmentFlowState,
} from '../../../../core/services/shipment-flow-state.service';
import { readPageSkeletonPreviewQueryFlag } from '../../../../shared/utils/e2e-query-flags';
import { DelayedLoadingController } from '../../../../shared/utils/delayed-loading';
import { environment } from '../../../../../environments/environment';
import { mapAddressValidationTokenToServiceAbilityRequest } from '../../../ship-from-ship-to/utils/map-shipment-addresses-to-service-ability-request';
import {
  applyRatedSpecialHandlingPieceCounts,
  buildEstimateManagerPackagePayload,
  buildOrchestratorAccessorialFlags,
  collectProductSelectionAttestations,
  countSpecialHandlingPiecesByCode,
  formatShipmentDateForPsa,
  mapOrchestratorDatesToMergeInput,
  type OrchestratorAccessorialFlags,
  type OrchestratorSignatureRequirement,
} from './build-delivery-service-requests';
import { DeliveryDateColumnComponent } from './delivery-date-column';
import type { SelectedDeliveryService } from './delivery-service-selection.model';
import {
  buildEstimateBreakdown,
  buildServiceMatrix,
  pickDefaultEndOfDayOffering,
  resolveFastestProductCode,
  resolveMostEconomicalProductCode,
  resolveServiceDisplayName,
  type DeliveryDateColumn,
  type DeliveryServiceOffering,
  type EstimateBreakdownView,
  type EstimateOfferingInput,
} from './delivery-service-matrix';
import { DeliveryServiceSkeleton } from './delivery-service-skeleton/delivery-service-skeleton';
import { formatDeliveryDateLabel } from './format-delivery-date-label';
import { isReturnCourierProduct, mergeDeliveryOfferings } from './merge-delivery-offerings';
import { ServiceLegalese } from './service-legalese';

type LoadState = 'idle' | 'loading' | 'ready' | 'error';

interface LoadOfferingsContext {
  flow: ShipmentFlowState;
  accountLookupToken: string;
  serviceAbilityToken: string;
  serviceAvailabilityToken: string;
  packageSelectionToken: string;
  shipDate: string;
  packageTypeId: string;
  addresses: NonNullable<ShipmentFlowState['addresses']>;
  package: NonNullable<ShipmentFlowState['package']>;
}

interface CatalogProductsResult {
  productMasterItems: ProductSelectionItem[];
  productMaster: {
    productCode: string;
    serviceTimeEnglish?: string;
    serviceTimeFrench?: string;
    descEn?: string;
    descFr?: string;
  }[];
}

interface ServiceMatrixResult {
  matrix: DeliveryDateColumn[];
  inputs: EstimateOfferingInput[];
}

/**
 * Select Your Delivery Date & Service section: Product Master + estimates orchestrator matrix.
 *
 * Delivery dates and prices come from POST /estimates/estimate.
 * Commitment labels still come from Product Master serviceTimeEnglish / serviceTimeFrench.
 */
@Component({
  selector: 'app-delivery-service-selection',
  imports: [
    AlertComponent,
    TypographyComponent,
    ChipComponent,
    TranslatePipe,
    DeliveryDateColumnComponent,
    DeliveryServiceSkeleton,
    ServiceLegalese,
  ],
  templateUrl: './delivery-service-selection.html',
  styleUrl: './delivery-service-selection.scss',
})
export class DeliveryServiceSelection implements OnInit, OnDestroy {
  private readonly shipmentFlowState = inject(ShipmentFlowStateService);
  private readonly productMasterApi = inject(ProductMasterApiService);
  private readonly estimatesOrchestratorApi = inject(EstimatesOrchestratorApiService);
  private readonly serviceAbilityApi = inject(ServiceAbilityApiService);
  private readonly serviceAvailabilityApi = inject(ServiceAvailabilityApiService);
  private readonly analytics = inject(AnalyticsService);
  private readonly translation = inject(TranslationService);

  /** Emits whenever the selected offering changes so the parent can validate Save & Continue. */
  readonly selectionChange = output<SelectedDeliveryService | null>();

  /** Emits the Estimated Costs breakdown for the page-level sticky panel. */
  readonly breakdownChange = output<EstimateBreakdownView | null>();

  /**
   * Emits when the delayed delivery/costs skeleton should paint.
   * Parent swaps Estimated Costs from the same rate-call pending state.
   */
  readonly skeletonChange = output<boolean>();

  /**
   * Emits true while offerings have not settled (idle or loading).
   * Parent hides signature/coverage during the pre-skeleton delay so they do not flash.
   */
  readonly offeringsUnresolvedChange = output<boolean>();

  protected readonly loadState = signal<LoadState>('idle');
  protected readonly columns = signal<DeliveryDateColumn[]>([]);
  protected readonly activeDeliveryDate = signal<string | null>(null);
  protected readonly selectedProductCode = signal<string | null>(null);
  protected readonly fastestProductCode = signal<string | null>(null);
  protected readonly mostEconomicalProductCode = signal<string | null>(null);
  protected readonly offeringsByCode = signal<Map<string, DeliveryServiceOffering>>(new Map());
  protected readonly showSelectionError = signal(false);
  /** Non-blocking coupon validation warning from the orchestrator. */
  protected readonly couponWarning = signal(false);
  /**
   * True when the delayed delivery skeleton should paint (OM-711: 1s delay / 0.5s min hold).
   */
  protected readonly showDeliverySkeleton = signal(false);
  private readonly delayedOfferingsLoading = new DelayedLoadingController((show) => {
    this.showDeliverySkeleton.set(show);
    this.skeletonChange.emit(show);
  });
  /**
   * When true, keeps the delivery skeleton visible for E2E/QA preview (`?pageSkeletonPreview=1`).
   */
  private readonly pageSkeletonPreview = signal(readPageSkeletonPreviewQueryFlag());
  /** Latest accessorial flags to include on orchestrate calls. */
  private readonly accessorialFlags = signal<OrchestratorAccessorialFlags>({});
  /**
   * True once the flags reflect the customer's current Service-step choices, so cleared
   * coverage or signature is not overwritten by the values persisted on a previous visit.
   */
  private accessorialFlagsResolved = false;
  /** Generation token so overlapping reloads discard stale responses. */
  private loadGeneration = 0;
  /** Cached Product Master catalog from the last successful full load. */
  private cachedCatalog: CatalogProductsResult | null = null;
  /** Cache key for inputs that invalidate the Product Master catalog. */
  private cachedCatalogKey: string | null = null;
  /** Ship date used for the latest successful Estimates Manager call (YYYY-MM-DD). */
  private lastEstimateShipDate: string | null = null;

  protected readonly helpdeskPhone = environment.helpdeskPhone;

  /**
   * True while Product Master + estimates have not settled (success or error).
   */
  protected readonly offeringsPending = computed(() => this.loadState() === 'loading');

  /**
   * True before the first ready/error settle, including idle before ngOnInit starts the load.
   */
  protected readonly offeringsUnresolved = computed(() => {
    if (this.pageSkeletonPreview()) {
      return true;
    }

    const state = this.loadState();
    return state === 'idle' || state === 'loading';
  });

  constructor() {
    effect(() => {
      if (this.pageSkeletonPreview()) {
        this.delayedOfferingsLoading.setPending(true);
        return;
      }

      const state = this.loadState();
      if (state === 'loading') {
        this.delayedOfferingsLoading.setPending(true);
        return;
      }
      if (state === 'error') {
        this.delayedOfferingsLoading.fail();
        return;
      }
      if (state === 'ready') {
        this.delayedOfferingsLoading.setPending(false);
      }
    });
    effect(() => {
      this.offeringsUnresolvedChange.emit(this.offeringsUnresolved());
    });
  }

  protected readonly blockingErrorMessage = computed(() => {
    this.translation.revision();
    return this.translation.translate(
      'createShipment.serviceStep.deliveryService.errors.estimateUnavailable',
      { helpdesk: this.helpdeskPhone },
    );
  });

  protected readonly selectedBreakdown = computed<EstimateBreakdownView | null>(() => {
    const code = this.selectedProductCode();
    if (!code) {
      return null;
    }

    const offering = this.offeringsByCode().get(code);
    if (!offering) {
      return null;
    }

    const packageValue = this.shipmentFlowState.load()?.package;
    const specialHandlingPieceCounts = packageValue
      ? countSpecialHandlingPiecesByCode(packageValue, {
          destinationResidential: this.shipmentFlowState.load()?.addresses?.shipTo?.residential,
        })
      : undefined;

    return buildEstimateBreakdown(offering.pricing, {
      specialHandlingPieceCounts: applyRatedSpecialHandlingPieceCounts(
        specialHandlingPieceCounts,
        offering.ratedSpecialHandlingPieceCounts,
      ),
      discountedPricing: offering.discountedPricing,
      discountRate: offering.couponDiscount?.discountRate,
    });
  });

  /**
   * Formats each matrix date for the compact mobile/tablet date chips.
   */
  protected formatDateChipLabel(deliveryDate: string): string {
    this.translation.revision();
    const locale = this.translation.locale() === 'fr' ? 'fr' : 'en';
    return formatDeliveryDateLabel(deliveryDate, locale);
  }

  /**
   * Selects which delivery-date column is visible on compact breakpoints.
   */
  protected onSelectDeliveryDate(deliveryDate: string): void {
    this.activeDeliveryDate.set(deliveryDate);
  }

  /**
   * Loads Product Master catalog items, then priced options from the estimates orchestrator.
   */
  ngOnInit(): void {
    void this.loadOfferings();
  }

  /**
   * Clears delayed skeleton timers when leaving the Service delivery section.
   */
  ngOnDestroy(): void {
    this.delayedOfferingsLoading.destroy();
  }

  /**
   * Returns whether a service offering is currently selected.
   */
  hasSelection(): boolean {
    return this.selectedProductCode() !== null;
  }

  /**
   * Marks selection as required for Save & Continue validation.
   */
  showRequiredSelectionError(): void {
    this.showSelectionError.set(true);
  }

  /**
   * Updates shipment accessorial flags and reloads priced offerings.
   * Backend maps flags to RES/ZS50/COV; special handling is derived server-side.
   */
  refreshWithShipmentServices(input: {
    signatureRequirement?: OrchestratorSignatureRequirement | null;
    declaredValue?: number | null;
  }): void {
    const nextFlags = buildOrchestratorAccessorialFlags(input);
    const unchanged = this.areAccessorialFlagsEqual(this.accessorialFlags(), nextFlags);
    this.accessorialFlagsResolved = true;
    if (unchanged) {
      return;
    }
    this.accessorialFlags.set(nextFlags);
    void this.reloadOfferingsForAccessorials();
  }

  /**
   * Selects a service card and persists the choice in shipment flow state.
   */
  protected onSelect(productCode: string): void {
    const offering = this.offeringsByCode().get(productCode);
    if (!offering) {
      return;
    }

    this.selectedProductCode.set(productCode);
    this.showSelectionError.set(false);
    const selection = this.toSelectedService(offering);
    this.shipmentFlowState.saveSelectedService(selection);
    this.emitSelectionState(selection);
    this.analytics.trackEvent({
      event: 'Services Details.',
      selectedServiceDetail: productCode,
    });
  }

  /**
   * Notifies the parent of the current selection and matching Estimated Costs breakdown.
   */
  private emitSelectionState(selection: SelectedDeliveryService | null): void {
    this.selectionChange.emit(selection);
    this.breakdownChange.emit(selection ? this.selectedBreakdown() : null);
  }

  /**
   * Reloads priced offerings using the cached Product Master catalog when still valid.
   * Falls back to a full catalog load when the cache key is missing or stale.
   * Keeps the existing matrix mounted while a refresh is in flight so scroll position
   * is not lost when Declared Value or signature changes (stale-while-revalidate).
   */
  private async reloadOfferingsForAccessorials(): Promise<void> {
    const generation = ++this.loadGeneration;
    const keepMatrixVisible = this.loadState() === 'ready' && this.columns().length > 0;
    if (!keepMatrixVisible) {
      this.loadState.set('loading');
    }
    this.couponWarning.set(false);

    const context = this.resolveLoadContext(this.shipmentFlowState.load());
    if (!context) {
      this.failLoad();
      return;
    }

    try {
      const cacheKey = this.buildCatalogCacheKey(context);
      const catalog =
        this.cachedCatalog && this.cachedCatalogKey === cacheKey ? this.cachedCatalog : null;

      const matrixResult = catalog
        ? await this.buildServiceMatrixFromCatalog(
            context,
            catalog,
            context.flow.attestations?.productSelectionByProductCode ?? {},
            generation,
          )
        : await this.fetchCatalogAndServiceMatrix(context, generation);

      if (!matrixResult) {
        return;
      }

      this.applyLoadedOfferings(context.flow, matrixResult);
      this.loadState.set('ready');
    } catch {
      this.failLoad();
    }
  }

  /**
   * Loads package selection, Product Master product details, then orchestrated estimates.
   * Requires the account attestation minted on Package Save.
   * Re-mints SA→PSA for today's ship date first so a prior Review pickup-date re-estimate
   * (OM-754) cannot leave future-dated tokens that desync shipmentDate vs deliveryDate.
   */
  private async loadOfferings(): Promise<void> {
    const generation = ++this.loadGeneration;
    this.loadState.set('loading');
    this.couponWarning.set(false);

    const context = this.resolveLoadContext(this.shipmentFlowState.load());
    if (!context) {
      this.failLoad();
      return;
    }

    try {
      const refreshedContext = await this.refreshServiceAbilityForTodayShipDate(context);
      if (!refreshedContext) {
        this.failLoad();
        return;
      }
      if (this.isStaleLoad(generation)) {
        return;
      }

      const matrixResult = await this.fetchCatalogAndServiceMatrix(refreshedContext, generation);
      if (!matrixResult) {
        return;
      }

      this.applyLoadedOfferings(refreshedContext.flow, matrixResult);
      this.loadState.set('ready');
    } catch {
      this.failLoad();
    }
  }

  /**
   * Re-runs service-ability → service-availability for today's PSA ship date and persists
   * the new tokens so Product Master / Estimates never reuse a pickup-future JWT_sa/JWT_psa.
   *
   * @returns Updated load context, or null when SA/PSA cannot be refreshed
   */
  private async refreshServiceAbilityForTodayShipDate(
    context: LoadOfferingsContext,
  ): Promise<LoadOfferingsContext | null> {
    const addressValidationToken = context.flow.attestations?.addressValidation?.trim() ?? '';
    if (!addressValidationToken) {
      return null;
    }

    const shipDate = formatShipmentDateForPsa();
    const serviceAbility = await this.serviceAbilityApi.check(
      mapAddressValidationTokenToServiceAbilityRequest(addressValidationToken, { shipDate }),
    );
    const serviceAbilityToken = serviceAbility.attestation?.trim() ?? '';
    if (!serviceAbility.serviceable || !serviceAbilityToken) {
      return null;
    }

    const serviceAvailability = await this.serviceAvailabilityApi.check({
      serviceAbilityToken,
    });
    const serviceAvailabilityToken = serviceAvailability.attestation?.trim() ?? '';
    if (!serviceAvailabilityToken) {
      return null;
    }

    this.shipmentFlowState.saveAttestation('serviceAbility', serviceAbilityToken);
    this.shipmentFlowState.saveAttestation('serviceAvailability', serviceAvailabilityToken);

    const flow = this.shipmentFlowState.load();
    if (!flow) {
      return null;
    }

    return {
      ...context,
      flow,
      shipDate,
      serviceAbilityToken,
      serviceAvailabilityToken,
    };
  }

  /**
   * Marks the section as failed and clears any persisted selection emission.
   */
  private failLoad(): void {
    this.loadState.set('error');
    this.emitSelectionState(null);
  }

  /**
   * Returns true when a newer loadOfferings call superseded this generation.
   */
  private isStaleLoad(generation: number): boolean {
    return generation !== this.loadGeneration;
  }

  /**
   * Builds a cache key for catalog inputs that would make Product Master results stale.
   */
  private buildCatalogCacheKey(context: LoadOfferingsContext): string {
    return [
      context.packageTypeId,
      context.shipDate,
      context.accountLookupToken,
      context.serviceAbilityToken,
      context.serviceAvailabilityToken,
      context.packageSelectionToken,
    ].join('|');
  }

  /**
   * Compares accessorial flag sets used on orchestrate requests.
   */
  private areAccessorialFlagsEqual(
    left: OrchestratorAccessorialFlags,
    right: OrchestratorAccessorialFlags,
  ): boolean {
    return (
      left.hasResidentialSignature === right.hasResidentialSignature &&
      left.hasAdultSignature === right.hasAdultSignature &&
      (left.declaredValue ?? null) === (right.declaredValue ?? null)
    );
  }

  /**
   * Validates wizard prerequisites and attestation tokens required before loading offerings.
   */
  private resolveLoadContext(flow: ShipmentFlowState | null): LoadOfferingsContext | null {
    if (
      !flow?.addresses ||
      !flow.package ||
      !flow.completedSteps.includes(SHIPMENT_WIZARD_STEPS.package)
    ) {
      return null;
    }

    const accountLookupToken = flow.attestations?.account?.trim();
    const serviceAbilityToken = flow.attestations?.serviceAbility?.trim();
    const serviceAvailabilityToken = flow.attestations?.serviceAvailability?.trim();
    const packageSelectionToken = flow.attestations?.packageSelection?.trim();

    if (
      !accountLookupToken ||
      !serviceAbilityToken ||
      !serviceAvailabilityToken ||
      !packageSelectionToken
    ) {
      return null;
    }

    return {
      flow,
      accountLookupToken,
      serviceAbilityToken,
      serviceAvailabilityToken,
      packageSelectionToken,
      shipDate: formatShipmentDateForPsa(),
      packageTypeId: flow.package.packageTypeId ?? '3',
      addresses: flow.addresses,
      package: flow.package,
    };
  }

  /**
   * Resolves accessorial flags from stored state or hydrates them from shipment services.
   * Hydration only happens once, so clearing coverage or signature is not overwritten by
   * the values persisted on a previous Service-step visit.
   */
  private resolveAccessorialFlagsForOrchestrate(
    flow: ShipmentFlowState,
  ): OrchestratorAccessorialFlags {
    if (this.accessorialFlagsResolved) {
      return this.accessorialFlags();
    }

    const flags = buildOrchestratorAccessorialFlags({
      signatureRequirement: flow.signatureProtection?.requirement,
      declaredValue: flow.additionalCoverage?.declaredValue ?? null,
    });
    this.accessorialFlags.set(flags);
    this.accessorialFlagsResolved = true;
    return flags;
  }

  /**
   * Loads Product Master catalog, Estimates Manager pricing, and the service matrix.
   */
  private async fetchCatalogAndServiceMatrix(
    context: LoadOfferingsContext,
    generation: number,
  ): Promise<ServiceMatrixResult | null> {
    const productMasterItems = await this.loadProductMasterItems({
      packageSelectionToken: context.packageSelectionToken,
      serviceAbilityToken: context.serviceAbilityToken,
    });

    if (this.isStaleLoad(generation)) {
      return null;
    }

    if (productMasterItems.length === 0) {
      this.failLoad();
      return null;
    }

    const productSelectionTokens = collectProductSelectionAttestations(productMasterItems);
    if (Object.keys(productSelectionTokens).length === 0) {
      this.failLoad();
      return null;
    }
    this.shipmentFlowState.saveProductSelectionAttestations(productSelectionTokens);

    const outboundProducts = productMasterItems.filter(
      (product) => !isReturnCourierProduct(product),
    );

    if (outboundProducts.length === 0) {
      this.failLoad();
      return null;
    }

    const catalog: CatalogProductsResult = {
      productMasterItems: outboundProducts,
      productMaster: outboundProducts.map((product) => ({
        productCode: product.productCode,
        serviceTimeEnglish: product.serviceTimeEnglish,
        serviceTimeFrench: product.serviceTimeFrench,
        descEn: product.descEn,
        descFr: product.descFr,
      })),
    };

    this.cachedCatalog = catalog;
    this.cachedCatalogKey = this.buildCatalogCacheKey(context);

    return this.buildServiceMatrixFromCatalog(context, catalog, productSelectionTokens, generation);
  }

  /**
   * Calls the Estimates Manager and builds the delivery-service matrix from the response.
   */
  private async buildServiceMatrixFromCatalog(
    context: LoadOfferingsContext,
    catalog: CatalogProductsResult,
    productSelectionTokens: Record<string, string>,
    generation: number,
  ): Promise<ServiceMatrixResult | null> {
    const accessorialFlags = this.resolveAccessorialFlagsForOrchestrate(context.flow);
    const tokens = Object.values(productSelectionTokens);
    if (tokens.length === 0) {
      this.failLoad();
      return null;
    }

    const orchestrateResponse = await this.estimatesOrchestratorApi.orchestrate({
      productSelectionTokens: tokens,
      packageSelectionToken: context.packageSelectionToken,
      serviceAbilityToken: context.serviceAbilityToken,
      serviceAvailabilityToken: context.serviceAvailabilityToken,
      accountLookupToken: context.accountLookupToken,
      ...buildEstimateManagerPackagePayload(context.package),
      ...accessorialFlags,
    });

    if (this.isStaleLoad(generation)) {
      return null;
    }

    this.couponWarning.set(
      orchestrateResponse.warnings?.some((warning) => warning.code === COUPON_VALIDATION_FAILED) ===
        true,
    );

    if (!orchestrateResponse.availableDeliveryDates?.length) {
      this.failLoad();
      return null;
    }

    const estimateAttestation = orchestrateResponse.attestation?.trim();
    if (estimateAttestation) {
      this.shipmentFlowState.saveEstimateAttestation(estimateAttestation);
    }

    const { psaProducts, estimates } = mapOrchestratorDatesToMergeInput(
      orchestrateResponse.availableDeliveryDates,
      estimateAttestation,
    );
    if (estimates.length === 0) {
      this.failLoad();
      return null;
    }

    const inputs = mergeDeliveryOfferings({
      shipDate: context.shipDate,
      psaProducts,
      productMaster: catalog.productMaster,
      estimates,
    });

    const locale = this.translation.locale() === 'fr' ? 'fr' : 'en';
    const matrix = buildServiceMatrix(inputs, locale);
    if (matrix.length === 0) {
      this.failLoad();
      return null;
    }

    this.lastEstimateShipDate = context.shipDate;
    return { matrix, inputs };
  }

  /**
   * Returns Product Master relations for a courier product loaded on this Service-step visit.
   */
  getProductRelations(productCode: string | null | undefined): ProductRelation[] {
    const code = productCode?.trim();
    if (!code) {
      return [];
    }

    const item = this.cachedCatalog?.productMasterItems.find(
      (product) => product.productCode === code,
    );
    return readProductRelations(item?.productRelations);
  }

  /**
   * Applies matrix results to component state and restores or defaults the selected offering.
   */
  private applyLoadedOfferings(flow: ShipmentFlowState, result: ServiceMatrixResult): void {
    const byCode = new Map<string, DeliveryServiceOffering>();
    for (const column of result.matrix) {
      for (const offering of column.offerings) {
        byCode.set(offering.productCode, offering);
      }
    }

    this.columns.set(result.matrix);
    this.activeDeliveryDate.set(result.matrix[0]?.deliveryDate ?? null);
    this.offeringsByCode.set(byCode);
    this.fastestProductCode.set(resolveFastestProductCode(result.inputs));
    this.mostEconomicalProductCode.set(resolveMostEconomicalProductCode(result.inputs));

    const persisted = flow.selectedService;
    const defaultOffering =
      (persisted && byCode.get(persisted.productCode)) ||
      pickDefaultEndOfDayOffering(result.inputs);

    if (defaultOffering) {
      this.selectedProductCode.set(defaultOffering.productCode);
      const selection = this.toSelectedService(defaultOffering);
      this.shipmentFlowState.saveSelectedService(selection);
      this.emitSelectionState(selection);
    } else {
      this.emitSelectionState(null);
    }
  }

  /**
   * Loads Product Master products for the attested package (JWT_pm + JWT_sa).
   * Returns an empty list when catalog lookup fails so the section shows the blocking error.
   */
  private async loadProductMasterItems(input: {
    packageSelectionToken: string;
    serviceAbilityToken: string;
  }): Promise<ProductSelectionItem[]> {
    try {
      return await this.productMasterApi.getProductSelection({
        packageSelectionToken: input.packageSelectionToken,
        serviceAbilityToken: input.serviceAbilityToken,
      });
    } catch {
      return [];
    }
  }

  /**
   * Maps a matrix offering into the persisted selected-service payload.
   */
  private toSelectedService(offering: DeliveryServiceOffering): SelectedDeliveryService {
    const locale = this.translation.locale() === 'fr' ? 'fr' : 'en';
    return {
      productCode: offering.productCode,
      deliveryDate: offering.deliveryDate,
      deliveryTime: offering.deliveryTime,
      guaranteed: offering.guaranteed,
      displayName: resolveServiceDisplayName(offering, locale),
      ...(this.lastEstimateShipDate ? { shipmentDate: this.lastEstimateShipDate } : {}),
      badge: this.resolveServiceBadge(offering.productCode),
      commitmentLabel: offering.commitmentLabel?.trim() || undefined,
      transportationMode: offering.transportationMode,
      pricing: offering.pricing,
      discountedPricing: offering.discountedPricing,
      couponDiscount: offering.couponDiscount,
      attestation: offering.attestation,
      ratedSpecialHandlingPieceCounts: offering.ratedSpecialHandlingPieceCounts,
    };
  }

  /**
   * Resolves the FASTEST / Most economical badge for a selected product code.
   */
  private resolveServiceBadge(productCode: string): SelectedDeliveryService['badge'] | undefined {
    if (this.fastestProductCode() === productCode) {
      return 'fastest';
    }
    if (this.mostEconomicalProductCode() === productCode) {
      return 'mostEconomical';
    }
    return undefined;
  }
}
